import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import { Checkbox } from '@webapp/common/components/checkbox';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { setHtml } from '@webapp/common/lib/ui';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import type { PerceptionTextItem } from '@webapp/common/resources/question';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './text-perception.css';

export const TextPerception: VFC<SurveyQuestion> = ({
    question: {
        responseActor,
        id,
        params: { perceptionText, cantRate: cantRateParam, cantRateText },
    },
}) => {
    const response = useSelector(responseActor, (r) => r.context);
    const cantRate = response?.value === CANTRATE_ID;
    const [selected, setSelected] = useState<Record<string, string>>({});
    const {
        design: {
            fonts: { elementColor },
        },
    } = useStateSelector(selectInfo);

    const htmlString = useMemo(
        () =>
            (perceptionText
                ? perceptionText?.map((item: PerceptionTextItem) => {
                      if (!item.isTag && item.isSelectable === undefined) {
                          return `<span class=${css.word} style="${
                              selected[item.pos] ? `background-color: ${elementColor}` : ''
                          }" data-pos=${item.pos}>${item.word}</span>`;
                      } else {
                          return item.word;
                      }
                  })
                : []
            ).join(''),
        [perceptionText, selected, elementColor],
    );

    const onWordClick = (e) => {
        const target = e.target as unknown as { dataset: { pos: string }; innerText: string };
        const pos = target?.dataset?.pos;
        const word = target?.innerText;

        if (pos) {
            setSelected((prev) => {
                const newValue = { ...prev };

                if (newValue[pos]) {
                    delete newValue[pos];
                } else {
                    newValue[pos] = word;
                }

                const mappedNewValue = Object.entries(newValue).map(([pos, word]) => ({
                    pos: Number(pos),
                    word,
                }));

                responseActor.send({ type: 'CHANGE', value: mappedNewValue as unknown as string, questionId: id });
                return newValue;
            });
        }
    };

    const handleCantRate = useCallback(
        (checked: boolean) => {
            setSelected({});
            responseActor.send({ type: 'CHANGE', value: checked ? CANTRATE_ID : '', questionId: id });
        },
        [responseActor, id],
    );

    return (
        <div className={css.wrap}>
            <div
                onClick={onWordClick}
                className={cn(CssQuestionType.QUESTION, CssQuestionType.TEXT_PERCEPTION)}
                {...setHtml(htmlString)}
            />
            {cantRateParam && (
                <div className={css.checkboxWrap}>
                    <Checkbox checked={cantRate} label={cantRateText} onChange={handleCantRate} />
                </div>
            )}
        </div>
    );
};
TextPerception.displayName = 'TextPerception';
